import { transition } from '@helpers/animate';
import { imageCoverContainer, imageWrapperPreserveRatio } from '@helpers/aspectRatio';
import brand, { ThemeOption } from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import fonts from '@helpers/fonts';
import { ButtonReset } from '@helpers/global';
import { baseGrid, siteWide } from '@helpers/grid';
import { Device, from, until } from '@helpers/media';
import scrollbars from '@helpers/scrollbars';
import srOnly from '@helpers/srOnly';
import { ButtonStyles } from '@stories/Components/Buttons/Button/Button.styles';
import { m } from 'framer-motion';
import { rgba } from 'polished';
import styled, { css } from 'styled-components';
const HeaderWrapper = styled.section `
  position: relative;
  height: 192px;
  ${transition('height')};

  &[data-shrink-header='true'] {
    height: 144px;
  }

  @media ${until(Device.DesktopSmall)} {
    height: ${fluid(85, 105)};
    transition: none;
  }
`;
const Container = styled.header `
  --backgroundColor: ${brand.white};
  --textColor: ${brand.black};
  --ctaAreaWidth: 0px;
  --navAreaWidth: 0px;
  ${transition('transform, box-shadow')};

  position: relative;
  top: 0;
  left: 0;
  width: 100%;

  &[data-show-bg='true'] {
    @media ${from(Device.DesktopSmall)} {
      transform: translateY(-60px);
    }
  }

  &[data-shrink-header='true'] {
    position: fixed;

    @media ${from(Device.DesktopSmall)} {
      box-shadow: 0 0 6px ${rgba(brand.black, 0.16)};
    }
  }

  @media ${until(Device.DesktopSmall)} {
    box-shadow: 0px 3px 5px 0px rgba(0, 0, 0, 0.16);
  }

  ${({ isEditMode }) => isEditMode
    ? css `
          z-index: 0;
        `
    : css `
          z-index: 9;
        `}
`;
const MobileBg = styled.div `
  ${transition('transform')};
  position: absolute;
  top: 50%;
  right: 42px;
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background-color: ${brand.white};

  &[data-expand='true'] {
    transform: scale(400);

    @media ${from(Device.TabletLarge)} {
      transform: scale(600);
    }
  }

  @media ${from(Device.DesktopSmall)} {
    display: none;
  }
`;
const TopArea = styled.div `
  ${siteWide};
  grid-row: 1;

  @media ${until(Device.DesktopSmall)} {
    display: none;
  }
`;
const TopInner = styled.div `
  position: relative;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 20px;
  padding-bottom: 30px;

  --containerWidth: calc(var(--siteWidth) + var(--sitePadding) + var(--sitePadding));
  --offsetWidth: calc(calc(100vw - var(--containerWidth)) / 2);

  &::after {
    content: '';
    position: absolute;
    bottom: 29px;
    right: calc(var(--sitePadding) * -1);
    width: calc(50vw + var(--sitePadding));
    min-width: 832px;
    height: 2px;
    transform: scaleY(0.5) translateX(var(--sitePadding));
    background-color: ${brand.grey.grey13};

    @media ${from(Device.DesktopLarge)} {
      transform: scaleY(0.5) translateX(var(--offsetWidth)) translateX(var(--sitePadding))
        translate(-9px);
    }
  }
`;
const Login = styled.div ``;
const LoginLink = styled.a `
  ${transition('color')};
  ${fonts.agBook.regular};
  font-size: 18px;
  line-height: 22px;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  color: ${brand.black};
  min-width: 130px;

  &:hover {
    color: ${brand.orange};
  }

  svg {
    height: 20px;
    margin-right: 10px;
  }
`;
const Member = styled.div `
  position: relative;
  color: ${brand.white};
`;
const MemberText = styled.span `
  ${fonts.agBook.regular};
  font-size: 20px;
  line-height: 24px;
  display: block;
  padding: 15px 30px;
  background-color: ${brand.orange};
  color: ${brand.white};
`;
const MemberCTA = styled.a `
  ${fonts.agBook.regular};
  font-size: 20px;
  line-height: 24px;
  display: block;
  padding: 15px 30px;
  background-color: ${brand.orange};
  color: ${brand.white};
  text-decoration: none;
`;
const MemberDropDown = styled.div `
  ${transition('opacity')};
  position: absolute;
  bottom: 1px;
  left: 50%;
  transform: translate(-50%, 100%) translateX(-10px);
  width: 290px;
  padding: 20px;
  z-index: 1;
  background-color: ${brand.orange};
  opacity: 0;
  max-height: 0;
  overflow: hidden;

  ${Member}:hover &,
  ${Member}:focus-within & {
    opacity: 1;
    max-height: 1000px;
  }
`;
const MemberPromoContent = styled.p `
  ${fonts.agBook.regular};
  font-size: 16px;
  line-height: 24px;
  margin-bottom: 15px;
`;
const MemberLink = styled.a `
  ${fonts.agBook.bold};
  display: inline-flex;
  gap: 20px;
  align-items: center;
  font-size: 16px;
  line-height: 24px;
  text-decoration: none;
  color: ${brand.white};

  &:hover,
  &:focus {
    svg {
      transform: translateX(5px);
    }
  }

  svg {
    ${transition('transform')};
    height: 26px;
  }
`;
const Inner = styled.div `
  ${siteWide};
`;
const Grid = styled.div `
  background-color: var(--backgroundColor);
  padding: 18px 0;
  position: relative;

  @media ${from(Device.DesktopSmall)} {
    padding: 0;
  }

  > ${Inner} {
    ${baseGrid};

    align-items: center;

    @media ${until(Device.DesktopSmall)} {
      gap: 12px;
    }
  }
`;
const LogoArea = styled.div `
  grid-column: 1 / span 2;

  @media ${until(Device.DesktopSmall)} {
    grid-column: 1 / span 5;
  }
`;
const DesktopLogo = styled.div `
  @media ${until(Device.DesktopSmall)} {
    display: none;
  }
`;
const MobileLogo = styled.div `
  @media ${from(Device.DesktopSmall)} {
    display: none;
  }
`;
const LogoWrapper = styled.a `
  display: block;

  &.focus-ring {
    outline: auto;
    outline-offset: 3px;
    outline-color: var(--textColor);
  }

  svg {
    ${transition('color, height, transform')};
    display: block;
    position: relative;
    height: ${fluid(33, 75)};
    color: var(--headerTextColor);

    @media ${from(Device.DesktopSmall)} {
      height: 155px;
      transform: translateX(-40px);

      ${Container}[data-shrink-header="true"] & {
        height: 110px;
        transform: translateX(-30px);
      }
    }

    [data-show-bg='true'] & {
      @media ${from(Device.DesktopSmall)} {
        height: 155px;
        color: ${brand.black};
      }
    }
  }
`;
const NavArea = styled.div `
  grid-column: 6 / span 7;
  display: grid;
  grid-template-rows: auto auto;
  position: relative;

  @media ${from(Device.DesktopSmall)} {
    grid-column: 3 / span 10;
    gap: 48px;
    align-self: end;
    ${transition('gap')};

    ${Container}[data-shrink-header="true"] & {
      gap: 0;
    }
  }
`;
const ActionWrapper = styled.div `
  display: flex;
  justify-content: flex-end;
  align-items: center;
  position: relative;
  right: -16px;
  gap: ${fluid(8, 36)};
  background-color: transparent;

  border-radius: 100000000px;
  padding: 0 2px;
  transition: background-color 0.5s;

  @media ${from(Device.DesktopSmall)} {
    grid-row: 2;
    position: relative;
    padding: 10px 2px;
  }
`;
const CtaArea = styled.div `
  display: flex;
  align-items: center;

  @media ${from(Device.DesktopSmall)} {
    gap: 20px;
  }
`;
const SearchArea = styled.div `
  position: relative;
`;
const SearchBtn = styled.button `
  ${transition('color')};
  color: ${brand.black};
  background-color: transparent;
  border: none;
  display: inline-flex;
  gap: 8px;
  place-content: center;
  place-items: center;

  &:hover {
    color: ${brand.orange};
  }

  ${ActionWrapper}[data-action-open='true'] & {
    display: none;
  }

  @media ${until(Device.DesktopSmall)} {
    display: none;
  }

  svg {
    height: 20px;
  }
`;
const SearchBox = styled.div `
  position: absolute;
  top: -8px;
  right: 0;
  background-color: ${brand.white};
  width: 360px;
  display: none;
  padding-right: 47px;

  &[data-show-search='true'] {
    display: block;
  }

  &:after {
    bottom: 100%;
    left: 50%;
    border: solid transparent;
    content: '';
    height: 0;
    width: 0;
    position: absolute;
    pointer-events: none;
    border-color: rgba(255, 255, 255, 0);
    border-bottom-color: ${brand.white};
    border-width: 8px;
    margin-left: -8px;
  }
`;
const SearchForm = styled.form `
  display: inline-flex;
  align-items: center;
  width: 100%;
`;
const SearchLabel = styled.label `
  ${srOnly};
`;
const SearchInput = styled.input `
  ${fonts.agBook.regular};
  font-size: 18px;
  line-height: 24px;
  margin-bottom: 0;
  background-color: transparent;
  border: none;
  border-bottom: 1px solid ${brand.orange};
  flex: 1 0 auto;
  padding: 0 5px;
  height: 26px;
`;
const SearchSubmit = styled.button `
  background-color: transparent;
  color: ${brand.orange};
  display: block;
  border: none;
  padding: 10px 10px 10px 0;

  svg {
    height: 20px;
  }
`;
const SearchClose = styled.button `
  background: transparent;
  border: none;
  padding: 0;

  svg {
    height: 19px;
    color: ${brand.orange};
  }
`;
const MobilePhoneBtn = styled.a `
  ${transition('color, background')};
  ${ButtonReset};
  color: var(--headerTextBgColor);
  width: 40px;
  height: 40px;
  position: relative;
  border-radius: 500000px;
  background: var(--headerTextColor);

  ${ActionWrapper}[data-action-open='true'] & {
    color: var(--headerTextColor);
    background: var(--headerTextBgColor);
  }

  > svg {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    display: block;
    height: 17px;
    width: 17px;
  }

  @media ${from(Device.DesktopSmall)} {
    display: none;
  }
`;
const MenuButton = styled.button `
  ${ButtonReset};
  color: ${brand.white};

  width: 40px;
  height: 40px;
  position: relative;
  border-radius: 500000px;
  background-color: ${brand.orange};

  order: 3;

  > svg {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    display: block;
    height: 22px;
    width: 22px;
  }

  @media ${from(Device.DesktopSmall)} {
    display: none;
  }
`;
const MenuTopBar = styled.div `
  ${transition('transform')};
  position: absolute;
  top: 13px;
  left: 50%;

  transform: translateX(-50%);

  width: 22px;
  height: 3px;
  background-color: currentColor;

  ${ActionWrapper}[data-action-open='true'] & {
    transform: translate(-50%, 6px) rotate(-45deg);
  }
`;
const MenuMiddleBar = styled.div `
  ${transition('opacity')};
  position: absolute;
  top: 19px;
  left: 50%;

  transform: translateX(-50%);

  width: 22px;
  height: 3px;
  background-color: currentColor;

  ${ActionWrapper}[data-action-open='true'] & {
    opacity: 0;
  }
`;
const MenuBottomBar = styled.div `
  ${transition('transform')};
  position: absolute;
  top: 25px;
  left: 50%;

  transform: translateX(-50%);

  width: 22px;
  height: 3px;
  background-color: currentColor;

  ${ActionWrapper}[data-action-open='true'] & {
    transform: translate(-50%, -6px) rotate(45deg);
  }
`;
const ContactBtn = styled.div `
  @media ${until(Device.DesktopSmall)} {
    display: none;
  }
`;
const MobileNavInner = styled.div `
  width: 100%;
`;
const PrimaryNavLinkStyles = css `
  ${fonts.agBook.regular};
  display: block;
  font-size: 20px;
  line-height: 24px;
  color: currentColor;
  text-decoration: none;

  &:focus {
    outline: none;
  }
`;
const NavLinkStyles = css `
  ${transition('text-decoration-color')};
  ${fonts.agBook.bold};
  font-size: 20px;
  line-height: 24px;
  color: ${brand.black};

  &:focus {
    outline: none;
  }

  &.focus-ring {
    outline: auto;
    outline-offset: 3px;
    outline-color: var(--textColor);
  }

  &.focus-ring,
  &:hover {
    text-decoration-color: currentColor;
  }
`;
const MobileNavButton = styled.button `
  ${ButtonReset};
  ${PrimaryNavLinkStyles};

  padding: 14px var(--sitePadding);
  position: relative;
  width: 100%;
  text-align: left;

  &:not(a) {
    &::before,
    &::after {
      display: block;
      content: '';
      position: absolute;
      right: var(--sitePadding);
      top: 50%;
      width: 14px;
      height: 2px;
      background-color: var(--textColor);
    }

    &[data-is-open='true']::after,
    &::before {
      transform: translateY(-50%);
    }

    &::after {
      ${transition('transform')};

      transform: translateY(-50%) rotate(90deg);
    }
  }

  &[data-indent='true'] {
    padding-left: calc(var(--sitePadding) * 2);
    background-color: var(--highlightColorPrimary);

    a& {
      font-weight: 400;
    }
  }
`;
const MobileNavLink = styled.a `
  ${NavLinkStyles};

  display: block;
  padding: 14px var(--sitePadding) 14px calc(var(--sitePadding) * 3);
  width: 100%;
`;
const MobileColumnsWrapper = styled(m.div) `
  overflow: hidden;
`;
const DesktopNav = styled.nav `
  --linkWidth: 0px;
  --linkPosX: 0px;
  --dropdownHeight: 0px;
  color: var(--textColor);
  flex: 1 0 auto;
  position: relative;

  > ${Inner} {
    display: flex;
    justify-content: flex-end;
    gap: ${fluid(20, 40)};
  }

  @media ${until(Device.DesktopSmall)} {
    display: none;
  }
`;
const DesktopNavItem = styled.li `
  &[data-component-theme=${ThemeOption.Orange}] {
    --themeColor: ${brand.orange};
  }

  &[data-component-theme=${ThemeOption.Blue}] {
    --themeColor: ${brand.blue};
  }

  &[data-component-theme=${ThemeOption.Purple}] {
    --themeColor: ${brand.purple};
  }

  &[data-component-theme=${ThemeOption.Red}] {
    --themeColor: ${brand.red};
  }

  &[data-component-theme=${ThemeOption.Green}] {
    --themeColor: ${brand.green.default};
  }

  &[data-component-theme=${ThemeOption.DarkGreen}] {
    --themeColor: ${brand.green.dark};
  }

  &[data-component-theme=${ThemeOption.Teal}] {
    --themeColor: ${brand.teal};
  }
`;
const DesktopPrimaryNavLink = styled.a `
  ${transition('border-color')};
  ${PrimaryNavLinkStyles};
  display: inline-flex;
  align-items: center;
  gap: 10px;
  color: ${brand.black};

  padding: 8px 0;

  &:hover {
    cursor: pointer;
  }
`;
const IconHolder = styled.div `
  position: relative;
  width: 12px;
  height: 12px;

  ${DesktopNavItem}:hover &,
  ${DesktopNavItem}[data-is-active='true'] &,
  .focus-ring & {
    svg {
      transform: rotate(-90deg);
    }
  }

  svg {
    ${transition('transform')};
    position: absolute;
    left: 0;
    top: 0;
    transform: rotate(90deg);
  }
`;
const DropdownMenuWrapper = styled.div `
  ${scrollbars};
  ${transition('opacity, max-height')};

  position: absolute;
  top: 100%;
  left: 0;
  max-height: 0;
  opacity: 0;
  width: 100%;
  border-radius: 0 0 16px 16px;
  overflow: hidden;

  ${DesktopNavItem}[data-is-active='true'] & {
    max-height: calc(100vh - var(--headerHeight));
    opacity: 1;
    overflow: visible;
  }

  > ${Inner} {
    min-height: 350px;
    padding: 0;
    position: relative;
    border-top: 10px solid var(--themeColor);
    box-shadow: 0px 2px 5px 0px rgba(0, 0, 0, 0.15);
    background-color: ${brand.white};
  }

  &[data-has-featured-item='false'] > ${Inner} {
    grid-template-columns: repeat(3, 1fr);
  }
`;
const IntroTitle = styled.h3 `
  ${fonts.agBook.bold};
  font-size: 24px;
  line-height: 28px;
  color: var(--themeColor);
  margin-bottom: 15px;
`;
const ChildTitle = styled.h4 `
  ${fonts.agBook.bold};
  font-size: 18px;
  line-height: 24px;
  color: ${brand.black};
`;
const DropdownMenuColumn = styled.div `
  padding: 35px 125px;

  &[data-first-featured-column='true'] {
    margin-left: auto;
  }
`;
const DropdownMenuTitle = styled.h3 `
  ${PrimaryNavLinkStyles};

  padding-bottom: 8px;
  margin: 10px 0 26px;
`;
const DropdownMenuLink = styled.a `
  ${NavLinkStyles};
  text-decoration: none;
  background-color: transparent;
  position: relative;
  margin-bottom: 15px;

  &:before {
    ${transition('transform')};
    content: '';
    position: absolute;
    top: 100%;
    right: 0%;
    background-color: ${brand.black};
    height: 1px;
    width: 100%;
    transform: scaleX(0);
    transform-origin: left center;
  }

  svg {
    ${transition('transform')};
    transform: translateY(8px);
    height: 14px;
    flex: 0 0 8px;
  }
`;
const DropdownMenuFeaturedLink = styled.a `
  ${PrimaryNavLinkStyles};

  display: inline-flex;
  gap: 10px;
  align-items: center;

  > svg {
    ${transition('transform')};

    width: 22px;
    height: 14px;
  }

  &.focus-ring {
    outline: auto;
    outline-offset: 3px;
    outline-color: var(--textColor);
  }

  &:hover {
    cursor: pointer;
  }

  &.focus-ring,
  &:hover {
    text-decoration-color: currentColor;

    > svg {
      transform: translateX(15%);
    }
  }
`;
const LinkColumn = styled.div `
  display: grid;
  grid-template-columns: repeat(3, 1fr);
`;
const DropdownMenuItem = styled.div `
  padding: 2px 0;
  border-radius: 8px;
  position: relative;
  grid-column: 1;
  margin-bottom: 15px;

  &:nth-child(n + 7) {
    grid-column: 2;
  }

  &:nth-child(n + 13) {
    grid-column: 3;
  }

  &:hover {
    ${DropdownMenuLink} {
      &:before {
        transform: scaleX(1);
      }
    }
  }

  > ${DropdownMenuFeaturedLink} {
    margin-bottom: 12px;
  }
`;
const PromotionItem = styled.div `
  grid-column: 3;
  grid-row: 1 / span 6;
  display: flex;
  justify-content: flex-end;

  [data-column-amount='3'] & {
    display: none;
  }

  @media ${until(Device.DesktopSmall)} {
    display: none;
  }
`;
const PromotionItemInner = styled.div `
  flex: 1 0 auto;
  max-width: 260px;

  ${ButtonStyles.Button} {
    width: 100%;
    justify-content: center;
    text-decoration: none;
  }
`;
const PromoMedia = styled.div `
  ${imageWrapperPreserveRatio(240, 180, 260, 195)}
  margin-bottom: 10px;
`;
const PromoImage = styled.img `
  ${imageCoverContainer()};
`;
const DropdownMultiLinkSection = styled.div `
  ${transition('height')};
  display: none;
  position: absolute;
  top: 30px;
  left: calc(100% - 30px);
  width: 370px;
  height: var(--dropdownHeight);
  padding: 40px;
  background-color: ${brand.blue};
  border-radius: 0 30px 30px 0;

  &[data-is-active='true'] {
    display: block;
  }

  ${DropdownMenuColumn}[data-no-group='false'] {
    padding: 10px 0 ${fluid(18, 24)};
  }

  ${DropdownMenuItem}[data-group-header='true'] {
    ${DropdownMenuLink} {
      padding: 5px 0;
      font-weight: 200;
    }
  }

  ${DropdownMenuItem}:hover {
    ${DropdownMenuLink} {
      transform: translateX(10px);
      background-color: ${brand.blue};

      &:before,
      &:after {
        background-color: ${brand.blue};
      }
    }
  }

  ${DropdownMenuColumn} {
    flex: 0 1 100%;
    flex-wrap: wrap;
    background-color: transparent;
    box-shadow: none;
    padding: 0;
    height: auto;
  }
`;
const HeaderStyles = {
    ActionWrapper,
    ChildTitle,
    ContactBtn,
    Container,
    CtaArea,
    DesktopLogo,
    DesktopNav,
    DesktopNavItem,
    DesktopPrimaryNavLink,
    DropdownMenuColumn,
    DropdownMenuFeaturedLink,
    DropdownMenuItem,
    DropdownMenuLink,
    DropdownMultiLinkSection,
    DropdownMenuTitle,
    DropdownMenuWrapper,
    Grid,
    HeaderWrapper,
    IconHolder,
    Inner,
    IntroTitle,
    LinkColumn,
    Login,
    LoginLink,
    LogoArea,
    LogoWrapper,
    Member,
    MemberDropDown,
    MemberLink,
    MemberPromoContent,
    MemberText,
    MemberCTA,
    MenuBottomBar,
    MenuButton,
    MenuMiddleBar,
    MenuTopBar,
    MobileBg,
    MobileColumnsWrapper,
    MobileLogo,
    MobileNavButton,
    MobileNavInner,
    MobileNavLink,
    MobilePhoneBtn,
    NavArea,
    PromoImage,
    PromoMedia,
    PromotionItem,
    PromotionItemInner,
    SearchArea,
    SearchBox,
    SearchBtn,
    SearchClose,
    SearchForm,
    SearchInput,
    SearchLabel,
    SearchSubmit,
    TopArea,
    TopInner,
};
export default HeaderStyles;
